import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { formatCurrency, formatDate, formatNumber } from '@/lib/format';
import { ORIGIN, type SubscriptionRow } from './types';

/**
 * The three cells that carry the meaning on this page: where the period stands,
 * how it came to exist, and what was actually paid.
 */

const ORIGIN_LABEL: Record<number, string> = {
  [ORIGIN.selfPurchase]: 'manageSubscriptions.origin.selfPurchase',
  [ORIGIN.adminAssigned]: 'manageSubscriptions.origin.adminAssigned',
  [ORIGIN.sponsored]: 'manageSubscriptions.origin.sponsored',
  [ORIGIN.gift]: 'manageSubscriptions.origin.gift',
  [ORIGIN.memberSeat]: 'manageSubscriptions.origin.memberSeat',
};

const ORIGIN_TONE: Record<number, 'default' | 'secondary' | 'warning' | 'outline'> = {
  [ORIGIN.selfPurchase]: 'default',
  [ORIGIN.adminAssigned]: 'warning',
  [ORIGIN.sponsored]: 'warning',
  [ORIGIN.gift]: 'outline',
  [ORIGIN.memberSeat]: 'secondary',
};

export function originLabelKey(origin: number | null | undefined) {
  if (origin === null || origin === undefined) {
    return ORIGIN_LABEL[ORIGIN.selfPurchase];
  }
  return ORIGIN_LABEL[origin] ?? ORIGIN_LABEL[ORIGIN.selfPurchase];
}

/**
 * Where the enrolment stands: whether it is running, and how much of its
 * period is left.
 *
 * `isActive` is stated outright rather than left to be inferred from the time
 * remaining. An inactive membership grants no benefits at all, and showing it
 * as a green "25 days left" made one look like it had six going spare when the
 * drawer — correctly — offered none.
 *
 * Cancelled and expired each already say the membership is not running, so
 * they stand alone; only a live period needs the state spelled out beside it.
 */
export function PeriodBadge({ row }: { row: SubscriptionRow }) {
  const { t } = useTranslation();

  if (row.cancelledAt) {
    return <Badge variant="secondary">{t('manageSubscriptions.cancelled')}</Badge>;
  }
  if ((row.daysRemaining ?? 0) < 0) {
    return <Badge variant="destructive">{t('manageSubscriptions.expired')}</Badge>;
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge variant={row.isActive ? 'success' : 'destructive'}>
        {row.isActive
          ? t('manageSubscriptions.active')
          : t('manageSubscriptions.inactive')}
      </Badge>
      {/* Muted when the membership is not running, so time left never reads as
          health on its own. */}
      <Badge
        variant={
          !row.isActive
            ? 'secondary'
            : row.isExpiringSoon
              ? 'warning'
              : 'success'
        }
      >
        {t('manageSubscriptions.daysRemaining', { count: row.daysRemaining ?? 0 })}
      </Badge>
    </span>
  );
}

/**
 * Origin as one tag.
 *
 * The pages this replaced showed bought-on-promo, was-sponsored and assigned-by
 * as three separate columns of green and red circles, which nobody new could
 * read.
 */
export function OriginCell({ row }: { row: SubscriptionRow }) {
  const { t } = useTranslation();
  const origin = row.origin ?? ORIGIN.selfPurchase;

  return (
    <div className="space-y-0.5">
      <Badge variant={ORIGIN_TONE[origin] ?? 'secondary'}>
        {t(originLabelKey(origin))}
      </Badge>
      {row.assignedByName && origin === ORIGIN.adminAssigned && (
        <p className="text-muted-foreground text-xs">{row.assignedByName}</p>
      )}
      {row.isMemberSeat && row.ownerName && (
        <p className="text-muted-foreground text-xs">{row.ownerName}</p>
      )}
      {row.boughtWithPromo && (
        <p className="text-muted-foreground text-xs">
          {t('manageSubscriptions.boughtWithPromo')}
        </p>
      )}
    </div>
  );
}

/**
 * What was paid — which is not a number in three of the five cases: a member
 * seat is covered by whoever bought the plan, a sponsored enrolment with no
 * payments was never charged, and an unbilled one is money not yet asked for.
 */
export function PaidCell({ row }: { row: SubscriptionRow }) {
  const { t } = useTranslation();

  if (row.isMemberSeat) {
    return (
      <span className="text-muted-foreground">
        {t('manageSubscriptions.coveredByBuyer')}
      </span>
    );
  }
  if (row.origin === ORIGIN.sponsored && (row.paymentCount ?? 0) === 0) {
    return (
      <span className="text-muted-foreground">
        {t('manageSubscriptions.noCharge')}
      </span>
    );
  }
  if ((row.paymentCount ?? 0) === 0) {
    return <Badge variant="warning">{t('manageSubscriptions.unbilled')}</Badge>;
  }

  return (
    <div>
      <p className="font-semibold tabular-nums">{formatCurrency(row.paidTotal)}</p>
      <p className="text-muted-foreground text-xs">
        {(row.paymentMethods ?? [])
          .map((method) => t(paymentMethodLabelKey(method)))
          .join(', ')}
        {row.lastPaymentAt ? ` · ${formatDate(row.lastPaymentAt)}` : ''}
      </p>
      {/* Muted and uncoloured: a discount that was granted, not money owed. */}
      {(row.amountVariance ?? 0) < 0 && (
        <p className="text-muted-foreground text-xs tabular-nums">
          {formatCurrency(Math.abs(row.amountVariance!))}{' '}
          {t('manageSubscriptions.underListPrice')}
        </p>
      )}
      {(row.refundedTotal ?? 0) > 0 && (
        <p className="text-muted-foreground text-xs tabular-nums">
          {t('manageSubscriptions.refunded')} {formatCurrency(row.refundedTotal)}
        </p>
      )}
    </div>
  );
}

/** "3 of 10 used", or just the count when the plan has no benefit allowance. */
export function BenefitsLabel({ row }: { row: SubscriptionRow }) {
  const { t } = useTranslation();
  const used = row.benefitsUsed ?? 0;
  const remaining = row.benefitsRemaining ?? 0;
  return (
    <>
      {used + remaining > 0
        ? t('manageSubscriptions.nOfMUsed', { used, total: used + remaining })
        : formatNumber(used)}
    </>
  );
}
